import type { KeyboardEvent, ReactNode } from 'react';

interface SelectableCardProps {
  className?: string;
  onSelect: () => void;
  /** Current selection state, exposed to assistive tech as `aria-pressed`. */
  selected?: boolean;
  children: ReactNode;
}

/**
 * Clickable selection chip/card that may CONTAIN interactive elements
 * (e.g. a visual <Checkbox>, which Radix renders as a <button>). A native
 * <button> wrapper would be invalid DOM nesting (button-in-button), so this
 * renders a keyboard-operable div instead.
 */
export function SelectableCard({ className, onSelect, selected, children }: SelectableCardProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={className}
      onClick={(e) => {
        // Radix form-control checkboxes re-dispatch a synthetic click on their
        // hidden <input> whenever `checked` flips; inside a <form> that click
        // bubbles up here and would re-select this tile, reverting the user's
        // actual choice. Only real pointer interactions select the tile.
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        onSelect();
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}
